import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit'
import { v4 as uuid } from 'uuid'

import {
  consumeAutoTriggerActionCreator,
  setActivityIdentityActionCreator,
} from '~/aioracle/slices/learning-context.slice'
import { logout } from '~/auth/slices/auth.slice'
import { SheetMode } from '~/aioracle/types/oracle-sheet.types'
import { ChatAiErrorMessageType } from '~/chat-scenario/types'
import { startListening } from '~/redux/listenerMiddleware'
import { RootState } from '~/redux/store'

interface ChatMessage {
  message: string
  isReceived: boolean
  image?: string
  // Set on an Oracle message the student didn't ask for, so the chat scrolls it into view regardless
  // of where they had scrolled to.
  isAutoTrigger?: boolean
  // The turn ended before the answer finished (`interrupted`, or no ending at all): the text is partial.
  isCutOff?: boolean
  // The student's message that the safety gate stopped: shown dimmed, labelled as not sent.
  isNotSent?: boolean
  sender: {
    nickname: string
    avatarPngUrl: string | null
  }
  id: string | number | undefined
}
interface ModalPosition {
  x: number
  y: number
  width: number
  height: number
  minWidth: number
  minHeight: number
  disableDragging: boolean
  disableResizing: boolean
  padding: number
  messageWidth: number
  windowWidth: number
  windowHeight: number
  isInitialized: boolean
  // Which layout the stored geometry was computed for, so a change of mode can re-place the panel.
  besideActivity: boolean
}
interface ChatAiState {
  isLoading: boolean
  stopMessage: boolean
  isShowingChatInitialModal: boolean
  isChatAiOpen: boolean
  isHintMessage: boolean
  chatAiConsent: boolean
  threadMessages: ChatMessage[]
  modalPosition: ModalPosition
  currentMessage: any
  errorMessageType: ChatAiErrorMessageType
  threadId: string
  // The activity the thread belongs to; undefined while the chat only belongs to a content page.
  threadActivityId?: string
  imageModal: {
    isImageModalOpen: boolean
    image: string
  }
  wasAutoTriggered: boolean
  // Opened by the app, not the student: the input must not steal focus from the activity
  wasOpenedAutomatically: boolean
  // The auto-trigger nudge still to animate; not persisted, so a reload doesn't replay it.
  freshNudgeId: ChatMessage['id'] | null
  showFollowUpMessage: boolean
  availabilityCount: number
  hideBottomMenu: boolean
  // A page with its own Oracle chat (the AI activity): a second Oracle input would fight it for the keyboard
  hasInlineOracle: boolean
  // Below lg the activity Oracle is a bottom sheet; the modal reserves room under the activity while it is half
  activitySheetMode: SheetMode
}

const modalInitialPosition = {
  x: 150,
  y: 205,
  width: 930,
  height: 550,
  minWidth: 420,
  minHeight: 350,
  disableDragging: false,
  disableResizing: false,
  padding: 72,
  messageWidth: 730,
  windowWidth: 0,
  windowHeight: 0,
  isInitialized: false,
  besideActivity: false,
}

const initialState: ChatAiState = {
  isLoading: false,
  stopMessage: false,
  isShowingChatInitialModal: false,
  isChatAiOpen: false,
  isHintMessage: false,
  chatAiConsent: false,
  threadMessages: [],
  currentMessage: {},
  modalPosition: modalInitialPosition,
  errorMessageType: null,
  threadId: uuid(),
  imageModal: {
    isImageModalOpen: false,
    image: '',
  },
  wasAutoTriggered: false,
  wasOpenedAutomatically: false,
  freshNudgeId: null,
  showFollowUpMessage: false,
  availabilityCount: 0,
  hideBottomMenu: false,
  hasInlineOracle: false,
  activitySheetMode: 'half',
}

const slice = createSlice({
  name: 'chat-ai',
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isLoading: action.payload,
    }),
    setStopMessage: (state, action: PayloadAction<boolean>) => ({
      ...state,
      stopMessage: action.payload,
    }),
    setIsInitialModalOpen: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isShowingChatInitialModal: action.payload,
    }),
    // Every open starts at half height: the student's work stays visible above the sheet
    setIsOpen: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isChatAiOpen: action.payload,
      wasOpenedAutomatically: false,
      activitySheetMode: 'half',
    }),
    openAutomatically: (state) => ({
      ...state,
      isChatAiOpen: true,
      wasOpenedAutomatically: true,
      activitySheetMode: 'half',
    }),
    setChatAiConsent: (state, action: PayloadAction<boolean>) => ({
      ...state,
      chatAiConsent: action.payload,
    }),
    setThreadMessage: (state, action: PayloadAction<ChatMessage>) => ({
      ...state,
      threadMessages: [...state.threadMessages.slice(-7), action.payload],
      freshNudgeId: action.payload.isAutoTrigger ? action.payload.id : state.freshNudgeId,
    }),
    clearFreshNudge: (state) => ({
      ...state,
      freshNudgeId: null,
    }),
    setCurrentMessage: (state, action: PayloadAction<any>) => ({
      ...state,
      currentMessage: action.payload,
    }),
    setErrorMessageType: (state, action: PayloadAction<ChatAiErrorMessageType>) => ({
      ...state,
      errorMessageType: action.payload,
    }),
    // A cleared chat is a new conversation: the Oracle keeps a thread's history server-side, so keeping
    // the thread_id would bring the cleared messages back.
    setThreadMessageEmpty: {
      reducer: (state, action: PayloadAction<string>) => ({
        ...state,
        threadMessages: [],
        threadId: action.payload,
        freshNudgeId: null,
      }),
      prepare: () => ({ payload: uuid() }),
    },
    // Another activity's thread: its messages and its "can I help you?" state belong to that activity.
    startActivityThread: {
      reducer: (state, action: PayloadAction<{ activityId: string; threadId: string }>) => ({
        ...state,
        threadMessages: [],
        threadId: action.payload.threadId,
        threadActivityId: action.payload.activityId,
        freshNudgeId: null,
        wasAutoTriggered: false,
      }),
      prepare: (activityId: string) => ({ payload: { activityId, threadId: uuid() } }),
    },
    setThreadActivityId: (state, action: PayloadAction<string | undefined>) => ({
      ...state,
      threadActivityId: action.payload,
    }),
    markMessageNotSent: (state, action: PayloadAction<ChatMessage['id']>) => ({
      ...state,
      threadMessages: state.threadMessages.map((message) =>
        message.id === action.payload ? { ...message, isNotSent: true } : message,
      ),
    }),
    setModalPosition: (state, action: PayloadAction<ModalPosition>) => ({
      ...state,
      modalPosition: action.payload,
    }),
    setImageModalOpen: (state, action: PayloadAction<{ isImageModalOpen: boolean; image: string }>) => ({
      ...state,
      imageModal: action.payload,
    }),
    setHintMessage: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isHintMessage: action.payload,
    }),
    setWasAutoTriggered: (state, action: PayloadAction<boolean>) => ({
      ...state,
      wasAutoTriggered: action.payload,
    }),
    setShowFollowUpMessage: (state, action: PayloadAction<boolean>) => ({
      ...state,
      showFollowUpMessage: action.payload,
    }),
    declareOracleAvailability: (state, action: PayloadAction<{ hideBottomMenu: boolean }>) => ({
      ...state,
      availabilityCount: state.availabilityCount + 1,
      hideBottomMenu: action.payload.hideBottomMenu,
    }),
    revokeOracleAvailability: (state) => ({
      ...state,
      availabilityCount: Math.max(0, state.availabilityCount - 1),
    }),
    setHasInlineOracle: (state, action: PayloadAction<boolean>) => ({
      ...state,
      hasInlineOracle: action.payload,
    }),
    setActivitySheetMode: (state, action: PayloadAction<SheetMode>) => ({
      ...state,
      activitySheetMode: action.payload,
    }),
  },
  extraReducers: (builder) => {
    // A fresh thread, not initialState's: that uuid is fixed at module load, so the next person on the
    // device (school sessionReset runs this case too) would continue the previous person's thread.
    // `logout` takes no payload to carry the uuid, hence the one impure reducer here.
    builder.addCase(logout, () => ({ ...initialState, threadId: uuid() }))
  },
})

export const selectIsChatAiLoading = (state: RootState): ChatAiState['isLoading'] => state.chatAi.isLoading
export const selectStopMessageChatAi = (state: RootState): ChatAiState['stopMessage'] => state.chatAi.stopMessage
export const selectIsChatAiInitialModalOpen = (state: RootState): ChatAiState['isChatAiOpen'] =>
  state.chatAi.isShowingChatInitialModal
export const selectIsChatAiOpen = (state: RootState): ChatAiState['isChatAiOpen'] => state.chatAi.isChatAiOpen
export const selectChatAiConsent = (state: RootState): ChatAiState['chatAiConsent'] => state.chatAi.chatAiConsent
export const selectChatAiThreadMessages = (state: RootState): ChatAiState['threadMessages'] =>
  state.chatAi.threadMessages
export const selectChatAiCurrentMessage = (state: RootState): ChatAiState['currentMessage'] =>
  state.chatAi.currentMessage
export const selectChatAiErrorMessageType = (state: RootState): ChatAiState['errorMessageType'] =>
  state.chatAi.errorMessageType
export const selectChatAiThreadId = (state: RootState): ChatAiState['threadId'] => state.chatAi.threadId
export const selectChatAiModalPosition = (state: RootState): ChatAiState['modalPosition'] => state.chatAi.modalPosition
export const selectImageModal = (state: RootState): ChatAiState['imageModal'] => state.chatAi.imageModal
export const selectImageModalOpen = (state: RootState): boolean => state.chatAi.imageModal.isImageModalOpen
export const selectIsHintMessage = (state: RootState): boolean => state.chatAi.isHintMessage
export const selectWasAutoTriggered = (state: RootState): boolean => state.chatAi.wasAutoTriggered
export const selectWasChatAiOpenedAutomatically = (state: RootState): boolean => state.chatAi.wasOpenedAutomatically
export const selectFreshNudgeId = (state: RootState): ChatAiState['freshNudgeId'] => state.chatAi.freshNudgeId
export const selectShowFollowUpMessage = (state: RootState): boolean => state.chatAi.showFollowUpMessage
export const selectIsOracleAvailable = (state: RootState): boolean => state.chatAi.availabilityCount > 0
export const selectOracleHideBottomMenu = (state: RootState): ChatAiState['hideBottomMenu'] =>
  state.chatAi.hideBottomMenu
export const selectHasInlineOracle = (state: RootState): boolean => state.chatAi.hasInlineOracle
export const selectActivitySheetMode = (state: RootState): SheetMode => state.chatAi.activitySheetMode
// ACTIONS
export const {
  setIsLoading: setChatAiIsLoadingActionCreator,
  setStopMessage: setSetStopMessageCreator,
  setIsInitialModalOpen: setChatAiIsOpenInitialModalActionCreator,
  setIsOpen: setChatAiIsOpenActionCreator,
  openAutomatically: openChatAiAutomaticallyActionCreator,
  setModalPosition: setChatAiModalPositionActionCreator,
  setThreadMessage: setThreadChatMessageActionCreator,
  setThreadMessageEmpty: setThreadChatMessageEmptyActionCreator,
  startActivityThread: startActivityThreadActionCreator,
  setThreadActivityId: setThreadActivityIdActionCreator,
  clearFreshNudge: clearFreshNudgeActionCreator,
  markMessageNotSent: markMessageNotSentActionCreator,
  setCurrentMessage: setCurrentMessageActionCreator,
  setChatAiConsent: setChatAiConsentActionCreator,
  setErrorMessageType: setChatAiErrorMessageTypeActionCreator,
  setImageModalOpen: setImageModalOpenActionCreator,
  setHintMessage: setHintMessageActionCreator,
  setWasAutoTriggered: setWasAutoTriggeredActionCreator,
  setShowFollowUpMessage: setShowFollowUpMessageActionCreator,
  declareOracleAvailability: declareOracleAvailabilityActionCreator,
  revokeOracleAvailability: revokeOracleAvailabilityActionCreator,
  setHasInlineOracle: setHasInlineOracleActionCreator,
  setActivitySheetMode: setActivitySheetModeActionCreator,
} = slice.actions

// Compares activityId only: a difficulty change re-fires the identity, and the modal's close clears it.
startListening({
  actionCreator: setActivityIdentityActionCreator,
  effect: ({ payload: { activityId } }, listenerApi) => {
    if (!activityId) return
    const { threadActivityId } = listenerApi.getState().chatAi
    if (threadActivityId === activityId) return
    if (!threadActivityId) {
      listenerApi.dispatch(setThreadActivityIdActionCreator(activityId))
      return
    }
    listenerApi.dispatch(startActivityThreadActionCreator(activityId))
    listenerApi.dispatch(consumeAutoTriggerActionCreator())
  },
})

// A cleared or student-written chat belongs to whatever is open now: an activity, or the content page.
startListening({
  matcher: isAnyOf(setThreadChatMessageEmptyActionCreator, setThreadChatMessageActionCreator),
  effect: (action, listenerApi) => {
    if (setThreadChatMessageActionCreator.match(action) && action.payload.isReceived) return
    const { chatAi, learningContext } = listenerApi.getState()
    if (chatAi.threadActivityId === learningContext.activityId) return
    listenerApi.dispatch(setThreadActivityIdActionCreator(learningContext.activityId))
  },
})

export default slice.reducer
